import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import SocialMovementSection from "../components/SocialMovementSection.jsx";

const STATUS_COLOR = {
  live: "sage-accent",
  in_house: "sage-accent",
  closed_alpha: "sage-accent",
  coming_soon: "sage-warn",
  planned: "sage-muted",
};
const STATUS_LABEL = {
  live: "LIVE",
  in_house: "LIVE · IN-HOUSE",
  closed_alpha: "CLOSED ALPHA",
  coming_soon: "COMING SOON",
  planned: "PLANNED",
};

export default function ProductsPage() {
  const [d, setD] = useState(null);
  const [free, setFree] = useState(null);
  const [filterQ, setFilterQ] = useState("");

  useEffect(() => {
    api.products().then(setD).catch(() => setD(null));
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sage/free-wall`)
      .then((r) => r.json()).then(setFree).catch(() => setFree(null));
  }, []);

  useEffect(() => {
    if (!d?.products) return;
    const origin = window.location.origin;
    const org = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Baker and Sage",
      "alternateName": "Sage",
      "url": origin,
      "description": "The Mender universal-cover engine. Music Studio. Sage-chat. Every reply billed in the open.",
      "legalName": "Baker and Sage",
    };
    const products = d.products.map((p) => ({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": p.name,
      "description": p.tagline || p.for_whom || "",
      "brand": { "@type": "Brand", "name": "Baker and Sage" },
      "url": p.route ? `${origin}${p.route}` : origin,
      "category": "SoftwareApplication",
    }));
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([org, ...products]);
    script.setAttribute("data-testid", "products-jsonld");
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
  }, [d]);

  const sage = d?.products?.find((p) => p.key === "sage");
  const paidRaw = d?.products?.filter((p) => p.paid) || [];
  // Founder ruling 2026-08-21: "The Founder Himself" is last and biggest on the paid page.
  const paid = [
    ...paidRaw.filter((p) => p.key !== "founder_himself"),
    ...paidRaw.filter((p) => p.key === "founder_himself"),
  ];
  const freeProducts = d?.products?.filter((p) => !p.paid) || [];
  const freeCount = freeProducts.length || free?.count || 0;
  const totalCount = (paid.length || 0) + freeCount;

  const filterHay = (p) => `${p.name || ""} ${p.tagline || ""} ${p.for_whom || ""} ${p.key || ""} ${(p.features_planned || []).join(" ")}`.toLowerCase();
  const needle = filterQ.trim().toLowerCase();
  const filteredPaid = !needle ? paid : paid.filter((p) => filterHay(p).includes(needle));
  const siblingSeed = [
    { slug: "purser", name: "Purser", tagline: "The payment processor. 0.5% flat on alternative rails, interchange-plus 0.5% on cards. PayPal-killer by design.", status: "Coming soon · Phase 1 armed", route: "/purser" },
    { slug: "mender", name: "Mender", tagline: "The universal-cover insurance engine. Renters + Pet + Cyber + Term Life at $900/yr · industry sells the same lines separately at $3,000–5,000.", status: "Coming soon · 8% profit floor · in polish", route: "/mender" },
    { slug: "tiny-homes", name: "Tiny Homes", tagline: "501(c)(3) charity to erase homelessness at the county level. Bottom-up county ordering — smallest problem first.", status: "Live · 501(c)(3)", route: "/tiny-homes" },
    { slug: "rice", name: "Rice The World", tagline: "The feeding valve. Delivery rails, Brotherhood workers, mission pool.", status: "Coming soon · model build in progress", route: "/rice-the-world" },
    { slug: "brotherhood", name: "Brotherhood of Misfits", tagline: "Hiring funnel for the vulnerable. A story and a willingness are enough.", status: "Coming soon", route: "/brotherhood" },
  ];
  const filteredSiblings = !needle ? siblingSeed : siblingSeed.filter((s) => `${s.name} ${s.tagline} ${s.slug}`.toLowerCase().includes(needle));

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The Product Line</div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4" data-testid="products-headline">
          {totalCount} Products. {freeCount} of them are free.
        </h1>
        <p className="text-base text-sage-muted leading-relaxed max-w-3xl mb-6 sage-prose">
          Every product carries the same honesty stack: cost meter, receipts, Warden, ledger row.
          Free things are free forever. Paid things are priced above our cost, not above your ability to breathe.
        </p>

        {/* ── HERO VIDEO · Founder ruling 2026-08-11 · 20% max screen area ─── */}
        <div className="mb-10 flex justify-start" data-testid="products-hero-video-wrap">
          <div className="max-w-[384px] w-full border-2 border-sage-accent">
            <video
              data-testid="products-hero-video"
              src="/shorts/master_cut_v1.mp4"
              poster="/logo.svg"
              controls
              muted
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-sage-pale"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            >
              <track kind="captions" src="/shorts/master_cut_v1.vtt" default />
            </video>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted bg-sage-pale/40 px-3 py-2 text-center">
              120s · watch how Sage works
            </div>
          </div>
        </div>

        {/* ── ASK-BOX · anonymous FAQ · cache-first · Founder ruling 2026-08-11 ─── */}
        <FAQAskBox />

        {/* ── LIVE KEYWORD FILTER · sift the 20+ products ─────────── */}
        <div className="mb-8 flex items-center gap-3" data-testid="products-filter-wrap">
          <input
            data-testid="products-filter-input"
            type="text"
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            placeholder="Filter the product line · try 'music' · 'video' · 'insurance' · 'payments'"
            className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
            maxLength={80}
          />
          <div className="text-[10px] uppercase tracking-[0.15em] text-sage-muted whitespace-nowrap" data-testid="products-filter-count">
            {filteredPaid.length + filteredSiblings.length} shown
          </div>
        </div>

        {/* ── TOP BANNERS — Free Wall + Sage-chat ─────────────────── */}
        <div className="grid md:grid-cols-2 gap-4 mb-12" data-testid="top-banners">
          <Link
            to="/free"
            data-testid="banner-free-wall"
            className="group relative overflow-hidden border-2 border-sage-accent bg-sage-pale hover:bg-sage-accent hover:text-sage-bg transition-colors duration-200 p-6 flex flex-col justify-between"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent group-hover:text-sage-bg mb-2">
                Free · {freeCount} doors
              </div>
              <div className="text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight">
                All Free Products, Services,<br />AI-Agents, and Resources
              </div>
              <p className="mt-3 text-sm sage-prose text-sage-muted group-hover:text-sage-bg">
                Free education. Free first-responder tools. Free mental healthcare. Free stadium seat. Everything the world overcharges for.
              </p>
            </div>
            <div className="mt-6 text-[11px] uppercase tracking-[0.2em] text-sage-accent group-hover:text-sage-bg">
              Open the free wall →
            </div>
          </Link>

          <Link
            to={sage?.route || "/sage"}
            data-testid="banner-sage-chat"
            className="group relative overflow-hidden border-2 border-sage-text bg-sage-text text-sage-bg hover:bg-sage-accent hover:border-sage-accent transition-colors duration-200 p-6 flex flex-col justify-between"
          >
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] mb-2 opacity-70">
                The honest AI chat · free tier available
              </div>
              <div className="text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight">
                Sage-chat
              </div>
              <p className="mt-3 text-sm sage-prose opacity-80">
                Cost meter on every reply. Refund button on every dollar. Warden grades the answer before you see it. No flattery.
              </p>
            </div>
            <div className="mt-6 text-[11px] uppercase tracking-[0.2em] opacity-90">
              Open Sage-chat →
            </div>
          </Link>
        </div>

        {/* ── PAID PRODUCTS ─────────────────────────────────────── */}
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Here's what we DO charge for</div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6">
          The paid product line
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-testid="paid-products">
          {filteredPaid.map((p) => {
            const c = STATUS_COLOR[p.status] || "sage-muted";
            const isHero = p.key === "founder_himself";
            return (
              <div
                key={p.key}
                data-testid={`product-${p.key}`}
                className={
                  isHero
                    ? "md:col-span-2 border-2 border-sage-accent bg-sage-pale/30 p-8 hover:border-sage-text transition-colors duration-150"
                    : "border border-sage-border p-6 hover:border-sage-accent transition-colors duration-150"
                }
              >
                {isHero && (
                  <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-3" data-testid="founder-himself-badge">
                    The house's most valuable product
                  </div>
                )}
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="text-sage-accent shrink-0" style={{ marginTop: 2 }}>
                      <ProductMark slug={p.key} size={isHero ? 64 : 40} />
                    </div>
                    <div className="min-w-0">
                      <div className={isHero ? "text-4xl md:text-5xl font-bold uppercase tracking-tighter text-sage-text" : "text-2xl font-bold uppercase tracking-tight text-sage-text"}>{p.name}</div>
                      <div className={isHero ? "text-base md:text-lg text-sage-text mt-2 sage-prose" : "text-xs text-sage-muted mt-1 sage-prose"}>{p.tagline}</div>
                    </div>
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.15em] border border-${c} text-${c} px-2 py-0.5 shrink-0 whitespace-nowrap`}>
                    {STATUS_LABEL[p.status]}
                  </div>
                </div>
                {isHero && p.challenge_line && (
                  <div className="text-xl md:text-2xl text-sage-text sage-prose leading-snug my-4" data-testid="founder-himself-challenge-card">
                    "{p.challenge_line}"
                  </div>
                )}
                {isHero && p.price_line && (
                  <div className="text-base text-sage-text sage-prose leading-relaxed mb-4 border-t border-b border-sage-accent/40 py-3">
                    {p.price_line}
                  </div>
                )}
                {p.engine_family && (
                  <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-2">
                    Engine · {p.engine_family}
                  </div>
                )}
                {p.for_whom && (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">For</div>
                    <div className={isHero ? "text-base text-sage-text sage-prose mb-4" : "text-sm text-sage-muted sage-prose mb-4"}>{p.for_whom}</div>
                  </>
                )}
                {p.features_planned && (
                  <>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-sage-faint mb-1">
                      {p.status === "live" || p.status === "in_house" ? "What it does" : "Planned"}
                    </div>
                    <ul className={isHero ? "text-base text-sage-text space-y-1 sage-prose mb-4" : "text-sm text-sage-muted space-y-1 sage-prose mb-4"}>
                      {p.features_planned.map((f, i) => <li key={i}>· {f}</li>)}
                    </ul>
                  </>
                )}
                <div className="flex gap-3 mt-3">
                  {p.route && (
                    <Link
                      to={p.route}
                      className={
                        isHero
                          ? "text-xs uppercase tracking-widest border-2 border-sage-accent bg-sage-accent text-sage-bg px-6 py-3 font-bold hover:bg-sage-text hover:border-sage-text"
                          : "text-[10px] uppercase tracking-[0.15em] border border-sage-accent text-sage-accent px-3 py-1 hover:bg-sage-accent hover:text-sage-bg"
                      }
                    >
                      {isHero ? "Request a week →" : "Enter →"}
                    </Link>
                  )}
                  {p.learn && p.learn !== p.route && (
                    <Link to={p.learn} className="text-[10px] uppercase tracking-[0.15em] border border-sage-border text-sage-muted px-3 py-1 hover:border-sage-text hover:text-sage-text">
                      Learn more
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── FAMILY OF APPS (siblings live at their own doors) ─── */}
        <div className="mt-16">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">The Family of Apps</div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight uppercase mb-6">
            Sibling houses · own domains · own doors
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-testid="family-apps">
            {filteredSiblings.map((s) => (
              <SiblingCard key={s.slug} slug={s.slug} name={s.name} tagline={s.tagline} status={s.status} route={s.route} />
            ))}
          </div>
        </div>
      </div>

      {/* ── SOCIAL MOVEMENT SECTION · BIG BOLD BOTTOM · Founder ruling 2026-02-13 ─── */}
      <SocialMovementSection />
    </div>
  );
}

function SiblingCard({ slug, name, tagline, status, route }) {
  // Map ProductsPage sibling slugs to ProductMark slugs.
  const markSlug = ({ rice: "sage", "tiny-homes": "sage", brotherhood: "sage" }[slug]) || slug;
  return (
    <Link
      to={route}
      data-testid={`sibling-${slug}`}
      className="block border border-sage-border p-5 hover:border-sage-accent transition-colors duration-150"
    >
      <div className="flex justify-between items-start mb-2 gap-3">
        <div className="flex items-center gap-3">
          <div className="text-sage-accent shrink-0"><ProductMark slug={markSlug} size={32} /></div>
          <div className="text-xl font-bold uppercase tracking-tight text-sage-text">{name}</div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] border border-sage-muted text-sage-muted px-2 py-0.5 shrink-0 whitespace-nowrap">
          {status}
        </div>
      </div>
      <div className="text-sm text-sage-muted sage-prose">{tagline}</div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-sage-accent">Open →</div>
    </Link>
  );
}

// ── FAQ Ask Box · anonymous · cache-first · Founder ruling 2026-08-11 ────
function FAQAskBox() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cached, setCached] = useState(false);

  const ask = async () => {
    if (q.trim().length < 3) return;
    setBusy(true);
    setAnswer(null);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sage/faq/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q.trim() }),
      });
      const d = await r.json();
      if (r.ok) {
        setAnswer(d.answer);
        setCached(!!d.cached);
      } else {
        setAnswer("Sage couldn't answer that one. Try rephrasing?");
      }
    } catch {
      setAnswer("Network hiccup. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") ask(); };

  return (
    <div className="mb-10 border border-sage-accent/40 bg-sage-pale/20 p-5" data-testid="faq-ask-box">
      <div className="text-[10px] uppercase tracking-[0.2em] text-sage-accent mb-2">
        Ask Sage · free · anonymous · answers cached for zero cost
      </div>
      <div className="flex gap-2">
        <input
          data-testid="faq-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="What is Mender? · How does Purser work? · Is Sage really free?"
          maxLength={500}
          className="flex-1 border border-sage-border bg-sage-bg px-3 py-2 text-sage-text focus:border-sage-accent outline-none"
        />
        <button
          onClick={ask}
          disabled={busy || q.trim().length < 3}
          data-testid="faq-submit"
          className="border-2 border-sage-accent bg-sage-accent text-sage-bg px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-sage-text hover:border-sage-text disabled:opacity-50 transition-colors"
        >
          {busy ? "…" : "Ask"}
        </button>
      </div>
      {answer && (
        <div className="mt-4 border-t border-sage-border pt-4" data-testid="faq-answer">
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted">Sage says</div>
            {cached && (
              <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent" data-testid="faq-cached-badge">
                Served from cache · 0 tokens burned
              </div>
            )}
          </div>
          <div className="text-sm text-sage-text sage-prose whitespace-pre-wrap">{answer}</div>
        </div>
      )}
    </div>
  );
}
